import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Button } from './ui/Button';
import type { User } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: User['role'];
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="loading-container" role="status" aria-label="Loading authentication">
        <LoadingSpinner size="lg" message="Checking authentication..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return (
      <div className="access-denied-page" role="alert">
        <div className="access-denied-card">
          <div className="access-denied-icon" aria-hidden="true">403</div>
          <h2 className="access-denied-title">Access Denied</h2>
          <p className="access-denied-message">
            You do not have permission to access this page.
            This area requires the <strong>{requiredRole}</strong> role.
          </p>
          <div className="access-denied-actions">
            <Button variant="primary" size="md" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default ProtectedRoute;
